import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus, Trophy, Calendar, Target, Zap } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface ChallengeAnalyticsProps {
  progressData: {
    completed_days: number;
    total_logged_days: number;
    daily_values: Array<{
      date: string;
      value: number;
      goal_met: boolean;
    }>;
  };
  challengeType: string;
  goalCriteria: any;
  participantCount: number;
}

export function ChallengeAnalytics({ 
  progressData, 
  challengeType, 
  goalCriteria,
  participantCount 
}: ChallengeAnalyticsProps) {
  const calculateStreak = () => {
    if (!progressData?.daily_values) return 0;
    
    const sortedDays = [...progressData.daily_values]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    let streak = 0;
    for (const day of sortedDays) {
      if (day.goal_met) streak++;
      else break;
    }
    return streak;
  };

  const calculateLongestStreak = () => {
    if (!progressData?.daily_values) return 0;
    
    const sortedDays = [...progressData.daily_values]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    let maxStreak = 0;
    let currentStreak = 0;
    
    for (const day of sortedDays) {
      if (day.goal_met) {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 0;
      }
    }
    return maxStreak;
  };

  const calculateCompletionRate = () => {
    if (!progressData?.daily_values || progressData.daily_values.length === 0) return 0;
    const completedDays = progressData.daily_values.filter(d => d.goal_met).length;
    return Math.round((completedDays / progressData.daily_values.length) * 100);
  };

  const calculateAverageValue = () => {
    if (!progressData?.daily_values || progressData.daily_values.length === 0) return 0;
    const sum = progressData.daily_values.reduce((acc, day) => acc + day.value, 0);
    return Math.round(sum / progressData.daily_values.length);
  };

  const getTrend = () => {
    if (!progressData?.daily_values || progressData.daily_values.length < 2) return 'stable';
    
    const recentDays = progressData.daily_values.slice(-7);
    const olderDays = progressData.daily_values.slice(-14, -7);
    
    if (olderDays.length === 0) return 'stable';
    
    const recentAvg = recentDays.reduce((acc, d) => acc + (d.goal_met ? 1 : 0), 0) / recentDays.length;
    const olderAvg = olderDays.reduce((acc, d) => acc + (d.goal_met ? 1 : 0), 0) / olderDays.length;
    
    if (recentAvg > olderAvg + 0.1) return 'up';
    if (recentAvg < olderAvg - 0.1) return 'down';
    return 'stable';
  };

  const getInsights = () => {
    const insights = [];
    const completionRate = calculateCompletionRate();
    const currentStreak = calculateStreak();
    const longestStreak = calculateLongestStreak();
    const trend = getTrend();

    if (completionRate >= 80) {
      insights.push({
        type: 'success',
        message: `Excellent consistency! You're completing ${completionRate}% of your goals.`,
        icon: Trophy,
      });
    } else if (completionRate >= 50) {
      insights.push({
        type: 'warning',
        message: `Good progress at ${completionRate}%. Try to increase consistency for better results.`,
        icon: Target,
      });
    } else {
      insights.push({
        type: 'info',
        message: `Room for improvement at ${completionRate}%. Small daily wins add up!`,
        icon: Zap,
      });
    }

    if (currentStreak >= 7) {
      insights.push({
        type: 'success',
        message: `Amazing ${currentStreak}-day streak! Keep the momentum going.`,
        icon: Zap,
      });
    }

    if (longestStreak > currentStreak && currentStreak === 0) {
      insights.push({
        type: 'warning',
        message: `Your best was ${longestStreak} days. You can get back there!`,
        icon: Target,
      });
    }

    if (trend === 'up') {
      insights.push({
        type: 'success',
        message: 'Your performance is trending upward! Great momentum.',
        icon: TrendingUp,
      });
    } else if (trend === 'down') {
      insights.push({
        type: 'warning',
        message: 'Performance is dipping. Refocus and get back on track!',
        icon: TrendingDown,
      });
    }

    return insights;
  };

  const currentStreak = calculateStreak();
  const longestStreak = calculateLongestStreak();
  const completionRate = calculateCompletionRate();
  const averageValue = calculateAverageValue();
  const trend = getTrend();
  const insights = getInsights();

  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-secondary" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-secondary" />
            Your Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Current Streak</p>
              <p className="text-2xl font-bold text-accent">{currentStreak}</p>
              <p className="text-xs text-muted-foreground">days</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Best Streak</p>
              <p className="text-2xl font-bold text-primary">{longestStreak}</p>
              <p className="text-xs text-muted-foreground">days</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold text-secondary">{completionRate}%</p>
              <Progress value={completionRate} className="h-1" />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">7-Day Trend</p>
              <div className="flex items-center gap-2">
                {getTrendIcon()}
                <p className="text-sm font-medium capitalize">{trend}</p>
              </div>
            </div>
          </div>

          {/* Insights */}
          {insights.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium">Insights & Recommendations</p>
              <div className="space-y-2">
                {insights.map((insight, index) => {
                  const Icon = insight.icon;
                  return (
                    <div 
                      key={index}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        insight.type === 'success' 
                          ? 'bg-secondary/5 border-secondary/20' 
                          : insight.type === 'warning'
                          ? 'bg-primary/5 border-primary/20'
                          : 'bg-accent/5 border-accent/20'
                      }`}
                    >
                      <Icon className={`h-4 w-4 mt-0.5 ${
                        insight.type === 'success' 
                          ? 'text-secondary' 
                          : insight.type === 'warning'
                          ? 'text-primary'
                          : 'text-accent'
                      }`} />
                      <p className="text-sm text-muted-foreground flex-1">{insight.message}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Comparison */}
          {participantCount > 1 && (
            <div className="space-y-3">
              <p className="text-sm font-medium">Compare with Group</p>
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Your completion rate:</span>
                  <Badge variant="secondary">{completionRate}%</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {completionRate >= 70 
                    ? "You're outperforming most participants! Keep it up." 
                    : completionRate >= 50
                    ? "You're doing well. A bit more consistency will put you at the top!"
                    : "Keep pushing! Every completed day brings you closer to your goal."
                  }
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
